import * as React from "react"
import { Link } from "react-router-dom"
import {
  type Category,
  type EntityCategory,
  useReadCategoryQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgChevronRight } from "@digits-shared/components/SVGIcons/line/ChevronRight.svg"
import { IconSize } from "@digits-shared/components/UI/Icons/Icon"
import { DigitsLinkButton } from "@digits-shared/DesignSystem/LinkButton"
import {
  HoverPopover,
  HoverPopoverArrow,
  HoverPopoverContent,
  HoverPopoverTrigger,
} from "@digits-shared/DesignSystem/Popovers/HoverPopover"
import { useHoverPopoverCloseOnClick } from "@digits-shared/DesignSystem/Popovers/useHoverPopoverCloseOnClick"
import colors from "@digits-shared/themes/colors"
import fonts, { BodyText, DetailText } from "@digits-shared/themes/typography"
import styled from "styled-components"
import { CategoryBreadcrumbs } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Shared/CategoryBreadcrumbs"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import { CategoryHoverIcon, CategoryIcon } from "src/shared/components/CategoryHover/CategoryIcon"
import transitions from "src/shared/config/transitions"
import { isCategory } from "src/shared/helpers/hierarchicalCategoryHelper"

const DEFAULT_CATEGORY_ICON_SIZE = IconSize.Medium
const CATEGORY_HOVER_WIDTH = 425

/*
  STYLES
*/

const CategoryHoverTrigger = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const StyledHoverArrow = styled(HoverPopoverArrow)`
  polygon {
    fill: ${colors.white};
  }
`

const CategoryInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
`

const CategoryName = styled(Link)`
  ${fonts.scale.h2};
  font-weight: ${fonts.weight.heavy};

  &:hover {
    text-decoration: underline;
  }
`

const DisplayNumber = styled(BodyText)`
  color: ${colors.secondary50};
`

const CategoryDescription = styled(DetailText)`
  margin-top: 2px;
  color: ${colors.secondary80};
  word-break: break-word;
`

const CategoryNameNumberText = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 8px;
`

const StyledHoverContent = styled(HoverPopoverContent)`
  padding: 0;
  transition: ${transitions.defaultFadeIn.duration} opacity;
  width: ${CATEGORY_HOVER_WIDTH}px;
  cursor: auto;
`

const ClickListener = styled.div``

const CategoryContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 16px;
  gap: 16px;
`

/*
  INTERFACES
*/

interface CategoryHoverProps {
  category: EntityCategory | Category
  neverHover?: boolean
  triggerContent?: React.ReactNode
  size?: IconSize
  align?: "start" | "center" | "end"
  side?: "top" | "right" | "bottom" | "left"
  sideOffset?: number
}

/*
  COMPONENT
*/

export const CategoryHover: React.FC<CategoryHoverProps> = ({
  category,
  neverHover,
  triggerContent,
  size = DEFAULT_CATEGORY_ICON_SIZE,
  align = "start",
  side = "bottom",
  sideOffset = 12,
}) => {
  const {
    hoverPopoverKey,
    onHoverPopoverOpenChange,
    clickListener,
    hideHoverPopover,
    pseudoTargetDirection,
    hoverPopoverContentRef,
    hoverPopoverTriggerRef,
  } = useHoverPopoverCloseOnClick()

  if (neverHover) {
    return (
      <CategoryHoverIcon size={size} subjectDisplayKey={category.displayKey || category.name} />
    )
  }

  return (
    <HoverPopover key={hoverPopoverKey} onOpenChange={onHoverPopoverOpenChange}>
      <HoverPopoverTrigger asChild>
        <CategoryHoverTrigger ref={hoverPopoverTriggerRef}>
          {triggerContent ? (
            triggerContent
          ) : (
            <CategoryHoverIcon
              size={size}
              subjectDisplayKey={category.displayKey || category.name}
            />
          )}
        </CategoryHoverTrigger>
      </HoverPopoverTrigger>

      <StyledHoverContent
        align={align}
        side={side}
        ref={hoverPopoverContentRef}
        pseudoTargetDirection={pseudoTargetDirection}
        sideOffset={sideOffset}
        onClick={hideHoverPopover}
      >
        <ClickListener onClick={clickListener}>
          <CategoryDetailsWithQuery category={category} />
        </ClickListener>
      </StyledHoverContent>
    </HoverPopover>
  )
}

const CategoryDetailsWithQuery: React.FC<{ category: EntityCategory | Category }> = ({
  category,
}) => {
  const viewKey = useViewVersion()
  const generatePath = useFrontendPathGenerator()
  const isCategoryType = React.useMemo(() => isCategory(category), [category])

  const { data } = useReadCategoryQuery({
    variables: {
      categoryId: category.id,
      viewKey,
    },
    skip: isCategoryType,
  })

  const categoryData = isCategoryType ? (category as Category) : data?.readCategory
  if (!categoryData) return null

  return (
    <>
      <StyledHoverArrow />
      <CategoryContainer>
        <CategoryIcon category={categoryData} />

        <CategoryInfo>
          <CategoryBreadcrumbs
            category={categoryData}
            route={routes.categoryDetails}
            allowClickPropagation
          />

          <CategoryNameNumberText>
            <CategoryName
              to={generatePath(routes.categoryDetails, { categoryId: categoryData.id })}
            >
              {categoryData.name}
            </CategoryName>
            {categoryData.displayNumber && (
              <DisplayNumber>{categoryData.displayNumber}</DisplayNumber>
            )}
          </CategoryNameNumberText>

          {categoryData.description && (
            <CategoryDescription>{categoryData.description}</CategoryDescription>
          )}
        </CategoryInfo>

        <DigitsLinkButton
          $variant="secondary-dark"
          to={generatePath(routes.categoryDetails, { categoryId: categoryData.id })}
          $circle
        >
          <SvgChevronRight />
        </DigitsLinkButton>
      </CategoryContainer>
    </>
  )
}
